import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import CarteCategorie from '../components/CarteCategorie'
import { Search, Loader2, ChevronLeft, BookOpen, GraduationCap } from 'lucide-react'
import { Link } from 'react-router-dom'

const Programmes = () => {
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const { data: programmes, isLoading } = useQuery({
    queryKey: ['programmes', selectedCategory],
    queryFn: async () => {
      if (!selectedCategory) return { items: [] }
      const params = { limit: 1000, statut: 'publié', categorie: selectedCategory }
      const res = await api.get('/programmes/', { params })
      return res.data
    },
    enabled: !!selectedCategory
  })

  const categories = [
    {
      name: "IA, Data Science & Data Analyst",
      description: "Maîtrisez l'intelligence artificielle, l'analyse de données et la data science pour transformer votre activité.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=800"
    },
    {
      name: "Finance & Comptabilité",
      description: "Audit, analyse financière, SYSCOHADA et gestion de trésorerie pour optimiser la performance de votre entreprise.",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=800"
    },
    {
      name: "RH & Management",
      description: "Gestion des talents, législation du travail et coaching pour renforcer votre capital humain.",
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=800"
    },
    {
      name: "Marketing & Vente",
      description: "Techniques de vente, marketing digital et relation client pour booster votre croissance commerciale.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800"
    },
    {
      name: "Informatique & Bureau",
      description: "Bureautique, cybersécurité, développement web et intelligence artificielle pour réussir votre transformation digitale.",
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=800"
    },
    {
      name: "Leadership & Stratégie",
      description: "Pilotage stratégique, prise de décision et développement personnel pour les leaders de demain.",
      image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=800"
    },
    {
      name: "Gestion de Projets",
      description: "Méthodologies, gestion des risques et suivi-évaluation pour garantir le succès de vos projets.",
      image: "https://images.unsplash.com/photo-1454165833767-1296b4bcfa58?q=80&w=800"
    },
    {
      name: "Logistique & Achats",
      description: "Supply chain, gestion des stocks et commerce international pour une logistique performante.",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=800"
    },
     {
      name: "Droit & Fiscalité",
      description: "Droit des affaires OHADA, fiscalité d'entreprise et gestion juridique des contrats.",
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=800"
    },
    {
      name: "Agriculture & Rural",
      description: "Techniques culturales, élevage et entrepreneuriat agricole pour le développement rural.",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=800"
    },
    {
      name: "Communication",
      description: "Communication d'entreprise, prise de parole en public et gestion de crise.",
      image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=800"
    },
    {
      name: "Secrétariat & Archives",
      description: "Efficacité professionnelle, archivage électronique et rédaction administrative.",
      image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=800"
    },
    {
      name: "Transit & Douane",
      description: "Transit, douane et commerce international pour vos opérations d'import-export.",
      image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=800"
    },
    {
      name: "Santé & Médical",
      description: "Gestion hospitalière, soins et marketing pharmaceutique pour les professionnels de santé.",
      image: "https://images.unsplash.com/photo-1505751172107-160351edca92?q=80&w=800"
    },
    {
      name: "Hôtellerie & Restauration",
      description: "Qualité de service, accueil et gestion hôtelière pour l'excellence touristique.",
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=800"
    },
    {
      name: "Éducation & Pédagogie",
      description: "Andragogie, pédagogie moderne et gestion d'établissements éducatifs.",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=800"
    }
  ]

  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName)
    window.scrollTo(0, 0)
  }

  return (
    <div className="pt-24 pb-16 bg-white min-h-screen">
      {/* Banner */}
      <div 
        className="text-white py-20 mb-12 relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: 'url("/images/presentation-cidh.jpeg")' }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-cidh-blue/80"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="flex flex-col items-center text-center">
            {selectedCategory ? (
               <button 
                 onClick={() => setSelectedCategory(null)}
                 className="flex items-center gap-2 text-cidh-gold mb-6 hover:underline font-semibold"
               >
                 <ChevronLeft size={20} /> Retour aux domaines
               </button>
            ) : (
               <div className="bg-cidh-gold p-3 rounded-2xl mb-6">
                  <GraduationCap size={40} className="text-cidh-blue" />
               </div>
            )}
            <h1 className="text-4xl md:text-6xl font-extrabold font-montserrat mb-6 tracking-tight">
              {selectedCategory || "Expertise Formation"}
            </h1>
            <p className="text-gray-300 max-w-2xl text-lg leading-relaxed">
              {selectedCategory 
                ? `Découvrez nos modules spécialisés en ${selectedCategory} conçus pour booster vos compétences.`
                : "Explorez nos différents domaines d'expertise et trouvez la formation qui propulsera votre carrière."
              }
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6">
        {!selectedCategory ? (
          <>
            {/* Search for Categories */}
            <div className="max-w-2xl mx-auto mb-16 px-4">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-cidh-blue transition-colors" size={24} />
                <input 
                  type="text" 
                  placeholder="Rechercher un domaine de formation..." 
                  className="w-full pl-14 pr-6 py-5 bg-white border-2 border-gray-100 rounded-2xl shadow-sm outline-none focus:border-cidh-blue focus:ring-4 focus:ring-cidh-blue/5 transition-all text-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {filteredCategories.map((cat, index) => (
                <CarteCategorie key={index} category={cat} onClick={handleCategoryClick} />
              ))}
            </div>
            
            {filteredCategories.length === 0 && (
              <div className="text-center py-24 text-gray-500 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                <Search size={48} className="mx-auto mb-4 opacity-20" />
                <p className="text-xl font-medium">Aucun domaine ne correspond à votre recherche.</p>
              </div>
            )}
          </>
        ) : (
          <div className="max-w-4xl mx-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-32 space-y-4">
                <Loader2 className="animate-spin text-cidh-blue" size={60} />
                <p className="text-gray-500 font-medium">Chargement des formations...</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-cidh-blue/5 p-8 border-b border-gray-100 flex items-center justify-between">
                   <h2 className="text-2xl font-bold text-cidh-blue flex items-center gap-3">
                      <BookOpen className="text-cidh-gold" size={28} />
                      Catalogue des formations
                   </h2>
                   <span className="bg-cidh-gold text-cidh-blue px-4 py-1.5 rounded-full font-bold text-sm">
                      {programmes?.total || 0} Modules
                   </span>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {programmes?.items.map((program) => (
                    <div 
                      key={program.id} 
                      className="flex items-center gap-4 p-5"
                    >
                      <div className="min-w-[40px] h-10 bg-cidh-blue/10 rounded-lg flex items-center justify-center text-cidh-blue font-bold">
                        {program.titre.split('-')[0].trim().split('.')[0]}
                      </div>
                      <div className="flex-grow">
                        <span className="text-lg font-medium text-gray-800">
                          {program.titre}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {programmes?.items.length === 0 && (
                  <div className="text-center py-20 text-gray-500">
                    Aucune formation disponible pour cette catégorie pour le moment.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Programmes
